import React, { useState } from 'react';
import {
  MenuItem, Select, FormControl, InputLabel,
  Chip, IconButton, Tooltip, Button, Popover
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import './JournalFilterBar.css';

const JournalFilterBar = ({
  filters,
  onFilterChange,
  instruments = [],
  sourceIds = [],
  accounts = [],
  onExport,
  onRefresh,
  onClearFilters,
  totalEntries = 0,
  filteredCount = 0,
  loading = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const dateRanges = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const activeFiltersCount = Object.values(filters).filter(v =>
    v && v !== 'all' && v !== ''
  ).length;

  const handleOpenFilters = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  return (
    <div className="journal-filter-bar">
      {/* Left side - Date range and quick filters */}
      <div className="filter-left">
        <FormControl size="small" className="date-range-select">
          <Select
            value={filters.dateRange || 'all'}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            displayEmpty
            startAdornment={<CalendarTodayIcon className="select-icon" />}
          >
            {dateRanges.map(range => (
              <MenuItem key={range.value} value={range.value}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Quick filter chips */}
        <div className="quick-filters">
          <Chip
            label="All"
            size="small"
            onClick={() => handleFilterChange('outcome', 'all')}
            className={filters.outcome === 'all' || !filters.outcome ? 'active' : ''}
          />
          <Chip
            label="Wins"
            size="small"
            onClick={() => handleFilterChange('outcome', 'win')}
            className={filters.outcome === 'win' ? 'active win' : ''}
          />
          <Chip
            label="Losses"
            size="small"
            onClick={() => handleFilterChange('outcome', 'loss')}
            className={filters.outcome === 'loss' ? 'active loss' : ''}
          />
        </div>

        {/* More filters button */}
        <Tooltip title="More filters">
          <IconButton
            onClick={handleOpenFilters}
            className={activeFiltersCount > 0 ? 'has-filters' : ''}
          >
            <FilterListIcon />
            {activeFiltersCount > 0 && (
              <span className="filter-badge">{activeFiltersCount}</span>
            )}
          </IconButton>
        </Tooltip>

        {/* Filters Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseFilters}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ className: 'filter-popover' }}
        >
          <div className="filter-popover-content">
            <div className="filter-popover-header">
              <span>Filters</span>
              <Button size="small" onClick={onClearFilters}>Clear All</Button>
            </div>

            <FormControl fullWidth size="small" className="filter-field">
              <InputLabel>Instrument</InputLabel>
              <Select
                value={filters.instrument || ''}
                onChange={(e) => handleFilterChange('instrument', e.target.value)}
                label="Instrument"
              >
                <MenuItem value="">All Instruments</MenuItem>
                {instruments.map(inst => (
                  <MenuItem key={inst} value={inst}>{inst}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {accounts.length > 0 && (
              <FormControl fullWidth size="small" className="filter-field">
                <InputLabel>Account</InputLabel>
                <Select
                  value={filters.account || ''}
                  onChange={(e) => handleFilterChange('account', e.target.value)}
                  label="Account"
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {accounts.map(acc => (
                    <MenuItem key={acc.account_id} value={String(acc.account_id)}>{acc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth size="small" className="filter-field">
              <InputLabel>Side</InputLabel>
              <Select
                value={filters.side || ''}
                onChange={(e) => handleFilterChange('side', e.target.value)}
                label="Side"
              >
                <MenuItem value="">All Sides</MenuItem>
                <MenuItem value="LONG">Long</MenuItem>
                <MenuItem value="SHORT">Short</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" className="filter-field">
              <InputLabel>Validation</InputLabel>
              <Select
                value={filters.validation || ''}
                onChange={(e) => handleFilterChange('validation', e.target.value)}
                label="Validation"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GO">GO Only</MenuItem>
                <MenuItem value="NO_GO">NO_GO Only</MenuItem>
                <MenuItem value="none">No Validation</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" className="filter-field">
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.source || ''}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                label="Source"
              >
                <MenuItem value="">All Sources</MenuItem>
                {sourceIds.map(src => (
                  <MenuItem key={src} value={src}>{src}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Popover>
      </div>

      {/* Right side - Results count and export */}
      <div className="filter-right">
        <span className="results-count">
          {filteredCount === totalEntries
            ? `${totalEntries} trades`
            : `${filteredCount} of ${totalEntries} trades`
          }
        </span>

        {activeFiltersCount > 0 && (
          <Tooltip title="Clear all filters">
            <IconButton size="small" onClick={onClearFilters} className="clear-btn">
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Refresh">
          <IconButton
            onClick={onRefresh}
            className={`refresh-btn ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Export to CSV">
          <IconButton onClick={onExport} className="export-btn">
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default JournalFilterBar;
